<script setup>
// NOTICE: KAPTION-INTEGRATION
// Campaign assistant selector dropdown for linking assistants to campaigns.
// Fetches available assistants from StudioChat via the proxy endpoint.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useRoute } from 'vue-router';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const route = useRoute();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const assistants = ref([]);
const isLoading = ref(false);
const isVisible = computed(() =>
  isKaptionFlagEnabled('KAPTION_CAMPAIGN_ASSISTANT_ROUTING')
);

const accountIdResolved = computed(
  () => props.accountId || route.params?.accountId
);

const assistantOptions = computed(() => {
  const options = [
    { value: null, label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT.NONE') },
  ];
  assistants.value.forEach(a => {
    options.push({
      value: a.base_id,
      label: `${a.name} (v${a.version_number})`,
    });
  });
  return options;
});

const selectedValue = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const fetchAssistants = async () => {
  if (!accountIdResolved.value) return;
  isLoading.value = true;
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountIdResolved.value}/kaption_campaign_assistants`
    );
    assistants.value = response.data || [];
  } catch (error) {
    assistants.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (isVisible.value) {
    fetchAssistants();
  }
});
</script>

<template>
  <div v-if="isVisible" class="flex flex-col gap-1">
    <label class="text-sm font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT.LABEL') }}
    </label>
    <ComboBox
      v-model="selectedValue"
      :options="assistantOptions"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT.PLACEHOLDER')"
      class="[&>div>button]:bg-n-alpha-black2"
    />
    <p class="text-xs text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT.HELP') }}
    </p>
  </div>
</template>
